/**
 * Assembly: turns realized windows into world placements (track + supports)
 * and the closed loop centerline. Pure data out — the seam with the renderer.
 */
import type {
  CurveSeg,
  Dir,
  LoopPath,
  LoopSegment,
  Placement,
  Vec3,
} from "../shared/types.ts";
import type { Skeleton } from "./topology.ts";
import type { ElevationPlan } from "./elevation.ts";
import type { StyleField } from "./style.ts";
import type { RealizedWindow } from "./wfc.ts";
import {
  applyXform,
  segLength,
  segPoint,
  windowXform,
  type Xform,
  xformSeg,
} from "./geom.ts";
import { windowCells } from "./registry.ts";
import { emitSupportStack } from "./supports.ts";

export interface AssembledTrack {
  placements: Placement[];
  loop: LoopPath;
  trackCells: Set<string>;
  maxDeckY: number;
}

export interface AssembleOptions {
  /**
   * Open path in the void (endless mode): no ground exists, so support
   * stacks, the goal banner, and solid ground-variants are all skipped —
   * those are ground concepts. Also disables the ring-wrap adjacency used
   * by solid support checks. Default (closed loop) is unaffected.
   */
  open?: boolean;
  /**
   * Endless ground vocabulary: local ground height at a cell index — an
   * island run cell riding its own terrace (null off-island or bridging
   * above it). When every cell of a window is grounded, solid piece
   * forms come back in open mode: an island top IS local ground.
   */
  localGroundY?: (cellIdx: number) => number | null;
  /**
   * Endless: island deck height at a world cell ("x,z" key) — the
   * planner's mass-claim record. Extends the solid neighbor check
   * beyond ring cells (the island deck abutting a shoreline turn).
   */
  deckAt?: (cellKey: string) => number | undefined;
}

const key = (x: number, z: number) => `${x},${z}`;

/**
 * Supports mimic real structural falsework rather than one-pillar-per-cell:
 * every contiguous elevated span gets a pillar at each end (abutments) and
 * periodic piers whenever the deck has run SUPPORT_SPAN since the last one.
 * A global clearance keeps pillars from doubling up where spans run close
 * together (hairpin columns, U-turn arms) — a nearby pillar visually carries
 * both decks.
 */
const SUPPORT_SPAN = 2.4;
const SUPPORT_CLEARANCE = 1.0;

export function assemble(
  skeleton: Skeleton,
  elevation: ElevationPlan,
  style: StyleField,
  windows: RealizedWindow[],
  opts: AssembleOptions = {},
): AssembledTrack {
  const placements: Placement[] = [];
  const segments: LoopSegment[] = [];
  const trackCells = new Set<string>();
  let totalLength = 0;
  const n = skeleton.cells.length;

  // Deck base height per RING cell — used to decide whether a solid
  // variant's extended surface actually abuts same-height deck. Ring cells
  // only: footprint extras (funnel flanks, bulge margins) are mostly void,
  // not deck, and must not count as support.
  const cellHeight = new Map<string, number>();
  skeleton.cells.forEach((c, i) =>
    cellHeight.set(key(c.x, c.z), elevation.baseY[i])
  );

  /**
   * A solid turn block reads as intended only when its extended surface
   * connects: some orthogonal neighbor of the footprint (not the two
   * ring-port cells, which touch every piece by construction) carries track
   * at the same base height.
   */
  const solidSupported = (win: RealizedWindow, xf: Xform): boolean => {
    const own = new Set<string>();
    const cells: [number, number][] = [];
    for (const [fx, fz] of win.def.footprint) {
      const w = applyXform(xf, [fx, 0, fz]);
      const cx = Math.round(w[0]);
      const cz = Math.round(w[2]);
      own.add(key(cx, cz));
      cells.push([cx, cz]);
    }
    // Open paths have no seam: clamp the port cells instead of wrapping
    // (wrapped indices would name bogus cells at both ends).
    const prevIdx = opts.open ? win.start - 1 : (win.start - 1 + n) % n;
    const nextIdx = opts.open
      ? win.start + win.def.moves.length
      : (win.start + win.def.moves.length) % n;
    const ports = new Set<string>();
    for (const idx of [prevIdx, nextIdx]) {
      const c = skeleton.cells[idx];
      if (c) ports.add(key(c.x, c.z));
    }
    const baseY = elevation.baseY[win.start % n];
    for (const [cx, cz] of cells) {
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const k = key(cx + dx, cz + dz);
        if (own.has(k) || ports.has(k)) continue;
        if (cellHeight.get(k) === baseY) return true;
        // Island deck abutting the footprint counts too (endless).
        if (opts.deckAt?.(k) === baseY) return true;
      }
    }
    return false;
  };

  const supportPrefix = style.supportColor ? "support-color" : "support";
  const supportCandidates: {
    x: number;
    z: number;
    height: number;
    dir: Dir;
    cradle: boolean;
    stretch: number;
    /** Stack base elevation (raised viaduct piers stand on the deck). */
    baseAt: number;
  }[] = [];
  let stretch = 0;

  // Twice-visited cells (crossings, bridge decks): the ground there belongs
  // to the lower channel — a pillar would stand in it. Piers land on the
  // neighboring deck cells instead. The goal banner also skips them.
  const crossingCells = new Set(
    skeleton.crossings.map(({ a }) =>
      key(skeleton.cells[a].x, skeleton.cells[a].z)
    ),
  );

  for (const win of windows) {
    const cell = skeleton.cells[win.start];
    const baseY = elevation.baseY[win.start];
    const xf: Xform = windowXform(cell.x, cell.z, cell.dirIn, baseY);
    const def = win.def;

    // Solids are ground-only (user rule). Closed loops: world ground at
    // baseY <= 0. Open paths: an island top IS local ground — the window
    // must sit entirely on its terrace (localGroundY matches every cell).
    const grounded = opts.open
      ? opts.localGroundY !== undefined &&
        Array.from(
          { length: def.moves.length },
          (_, j) => win.start + j,
        ).every((idx) => opts.localGroundY!(idx) === elevation.baseY[idx]) &&
        style.solidAt(0)
      : style.solidAt(baseY);
    const useSolid = grounded && def.solidId !== undefined &&
      (!def.solidExtends || solidSupported(win, xf));
    // −1 for noMesh windows: their loop segment has no placement of its own.
    const placementIndex = def.noMesh ? -1 : placements.length;
    const rotation = ((xf.r + def.meshRot) % 4) as Dir;
    // noMesh windows (a crossing's second visit) contribute centerline and
    // occupancy only — the shared cell's mesh comes from the first visit.
    if (!def.noMesh) {
      placements.push({
        pieceId: useSolid ? def.solidId! : (def.meshId ?? def.id),
        pos: applyXform(xf, def.meshOffset),
        rotation,
        ...(def.mirrored ? { mirrored: true } : {}),
        role: "track",
      });
    }
    for (const overlay of def.overlays ?? []) {
      // Overlay offsets are absolute entry-frame positions of the overlay
      // mesh origin (not relative to the base mesh).
      placements.push({
        pieceId: overlay.id,
        pos: applyXform(xf, overlay.offset),
        rotation: ((xf.r + (overlay.rot ?? 0)) % 4) as Dir,
        ...(overlay.mirrored ? { mirrored: true } : {}),
        role: "track",
      });
    }

    // Track cells (full footprint) for occupancy-aware decor later.
    for (const [fx, fz] of def.footprint) {
      const w = applyXform(xf, [fx, 0, fz]);
      trackCells.add(key(Math.round(w[0]), Math.round(w[2])));
    }

    // Centerline segments in world space.
    const curve: CurveSeg[] = def.centerline.map((s) => xformSeg(xf, s));
    const length = curve.reduce((acc, s) => acc + segLength(s), 0);
    const alt = def.altCenterline
      ? (() => {
        const altCurve = def.altCenterline!.map((s) => xformSeg(xf, s));
        return {
          curve: altCurve,
          length: altCurve.reduce((acc, s) => acc + segLength(s), 0),
        };
      })()
      : undefined;
    segments.push({
      placementIndex,
      curve,
      length,
      ...(alt ? { alt } : {}),
      ...(def.holes
        ? { holes: def.holes.map((h) => applyXform(xf, h)) }
        : {}),
      ...(def.junction ? { junction: def.junction } : {}),
    });
    totalLength += length;

    // Supports: flat elevated pieces get a stack per channel cell; climbs get
    // one stack at whichever end is elevated (entry for descents, exit-side
    // handled by the neighboring flat window). Void mode: piers exist ONLY
    // where the planner recorded a DECK below the whole window (raised
    // viaduct crossings, USER round) — the stacks stand on the island top;
    // everywhere else the void line floats.
    const deckBelow = opts.open && opts.deckAt !== undefined
      ? (() => {
        const cells = windowCells(def);
        let drop: number | undefined;
        for (let j = 0; j < cells.length; j++) {
          const idx = win.start + j;
          const c = skeleton.cells[idx % skeleton.cells.length];
          const top = opts.deckAt!(key(c.x, c.z));
          if (top === undefined) return undefined;
          const d = elevation.baseY[idx] - top;
          if (d < 0.499) return undefined;
          if (drop === undefined) drop = d;
          else if (Math.abs(drop - d) > 1e-6) return undefined;
        }
        return drop;
      })()
      : undefined;
    if (
      def.deckDelta === 0 &&
      (opts.open ? deckBelow !== undefined : baseY > 0)
    ) {
      const stackH = opts.open ? deckBelow! : baseY;
      const stackBase = opts.open ? baseY - deckBelow! : 0;
      const cells = windowCells(def);
      for (let j = 0; j < cells.length; j++) {
        const c = skeleton.cells[(win.start + j) % skeleton.cells.length];
        if (crossingCells.has(key(c.x, c.z))) continue;
        if (c.move === "F" && !def.columnSupports) {
          supportCandidates.push({
            x: c.x,
            z: c.z,
            height: stackH,
            dir: c.dirIn,
            cradle: true,
            stretch,
            baseAt: stackBase,
          });
        } else {
          // Turn cells and off-center channels (waves, bends, wide): a
          // pillar is only allowed where it actually touches deck, so anchor
          // it under the channel by snapping to the nearest centerline
          // point. Plain column — the cradle can't align to a diagonal
          // tangent or displaced lane.
          const p = nearestOnCurve(curve, c.x, c.z);
          supportCandidates.push({
            x: p[0],
            z: p[2],
            height: stackH,
            dir: c.dirIn,
            cradle: false,
            stretch,
            baseAt: stackBase,
          });
        }
      }
      // Per-branch diamond supports (M13): an elevated diamond's window
      // cells follow the MAIN branch, so its columns covered only that
      // lane — the OTHER branch deck hung unsupported. Snap a second
      // candidate line onto the alt centerline; the span/clearance greedy
      // thins both lanes together.
      // The alt lane runs PARALLEL to the main lane, so it must be its
      // own greedy group (stretch + 0.5): measured against main-lane
      // pillars it never accumulates a full span of gap and every
      // candidate is skipped. Clearance still dedupes the split/merge
      // mouths where the lanes converge.
      if (alt) {
        for (let j = 0; j < cells.length; j++) {
          const c = skeleton.cells[(win.start + j) % skeleton.cells.length];
          if (crossingCells.has(key(c.x, c.z))) continue;
          const p = nearestOnCurve(alt.curve, c.x, c.z);
          supportCandidates.push({
            x: p[0],
            z: p[2],
            height: stackH,
            dir: c.dirIn,
            cradle: false,
            stretch: stretch + 0.5,
            baseAt: stackBase,
          });
        }
      }
    } else {
      // Ground-level or climbing window: closes any elevated stretch.
      stretch++;
    }
  }

  // Goal gate: a banner straddling the channel marks the lap line — its
  // post feet land exactly on the rail tops (post centers ±0.425 = wall-top
  // centers, measured; cloth underside at +0.75 clears the marble by 0.6).
  // First plain straight window in ring order keeps it deterministic.
  // Endless mode has no lap line (segments hold no ceremony).
  for (const win of opts.open ? [] : windows) {
    if (win.def.id !== "straight") continue;
    const cell = skeleton.cells[win.start % n];
    if (crossingCells.has(key(cell.x, cell.z))) continue;
    placements.push({
      pieceId: "banner-high",
      pos: [cell.x, elevation.baseY[win.start % n] + 0.5, cell.z],
      rotation: ((1 - cell.dirIn + 4) % 4) as Dir,
      role: "track",
      goal: true,
    });
    break;
  }

  // Structural density rule (deterministic, no RNG): per elevated stretch,
  // keep the two ends plus piers at SUPPORT_SPAN intervals, subject to the
  // global clearance.
  const kept: { x: number; z: number }[] = [];
  const nearKept = (x: number, z: number, d: number) =>
    kept.some((k) => Math.hypot(k.x - x, k.z - z) < d);
  const byStretch = new Map<number, typeof supportCandidates>();
  for (const cand of supportCandidates) {
    const list = byStretch.get(cand.stretch) ?? [];
    list.push(cand);
    byStretch.set(cand.stretch, list);
  }
  for (const group of byStretch.values()) {
    let last: { x: number; z: number } | null = null;
    for (let i = 0; i < group.length; i++) {
      const cand = group[i];
      const isEnd = i === 0 || i === group.length - 1;
      const gap = last
        ? Math.hypot(cand.x - last.x, cand.z - last.z)
        : Infinity;
      if (!isEnd && gap < SUPPORT_SPAN) continue;
      if (nearKept(cand.x, cand.z, SUPPORT_CLEARANCE)) continue;
      kept.push({ x: cand.x, z: cand.z });
      last = cand;
      emitSupportStack(
        placements,
        supportPrefix,
        style.supportKind,
        cand.x,
        cand.z,
        cand.height,
        cand.dir,
        cand.cradle,
        cand.baseAt,
        // M19c pattern pool: LOOP mode only — endless (open) viaduct
        // stacks keep the classic pool (the golden tripwire).
        !opts.open,
      );
    }
  }

  return {
    placements,
    loop: { segments, totalLength },
    trackCells,
    maxDeckY: elevation.maxBaseY,
  };
}

/** Closest point on a window's world centerline to (x, z), horizontally. */
function nearestOnCurve(
  curve: readonly CurveSeg[],
  x: number,
  z: number,
): Vec3 {
  let best: Vec3 = [x, 0, z];
  let bestD = Infinity;
  for (const seg of curve) {
    for (let i = 0; i <= 24; i++) {
      const p = segPoint(seg, i / 24);
      const d = (p[0] - x) ** 2 + (p[2] - z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
  }
  return best;
}

/** Constant-speed sampling along the loop (s in world units, wraps). */
export function sampleLoop(
  loop: LoopPath,
  s: number,
): { pos: Vec3; tangent: Vec3 } {
  const total = loop.totalLength;
  let d = ((s % total) + total) % total;
  for (const seg of loop.segments) {
    if (d > seg.length) {
      d -= seg.length;
      continue;
    }
    // Locate within this segment's curve parts.
    for (const part of seg.curve) {
      const len = segLength(part);
      if (d > len) {
        d -= len;
        continue;
      }
      const t = len === 0 ? 0 : d / len;
      const p = segPoint(part, t);
      const q = segPoint(part, Math.min(1, t + 0.01));
      const tangent: Vec3 = norm([q[0] - p[0], q[1] - p[1], q[2] - p[2]]);
      return { pos: p, tangent };
    }
    break;
  }
  // Numeric edge (d ≈ total): return the loop start.
  const first = loop.segments[0].curve[0];
  const p = segPoint(first, 0);
  const q = segPoint(first, 0.01);
  return { pos: p, tangent: norm([q[0] - p[0], q[1] - p[1], q[2] - p[2]]) };
}

function norm(v: Vec3): Vec3 {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}
